import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useCanonicalUrl } from "shared/hooks";

import { PageSeo } from "scmp-app/components/meta";
import { getVideoDescription } from "scmp-app/components/video/helpers";
import type { videoSeoVideo$key } from "scmp-app/queries/__generated__/videoSeoVideo.graphql";

type Props = {
  reference: videoSeoVideo$key;
};

export const VideoSeo: FunctionComponent<Props> = ({ reference }) => {
  const video = useFragment(
    graphql`
      fragment videoSeoVideo on Video {
        headline
        urlAlias
        descriptionBody: body {
          text
        }
        subHeadline {
          text
        }
        summary {
          text
        }
        image(filter: { type: MAIN_IMAGE }) {
          style(filter: { style: "wide_landscape" }) {
            url
          }
        }
      }
    `,
    reference,
  );

  const { canonicalUrl } = useCanonicalUrl(video.urlAlias);

  const description = getVideoDescription({
    body: video.descriptionBody?.text,
    subHeadline: video.subHeadline?.text,
    summary: video.summary?.text,
  });

  const imageUrl = video.image?.style?.url;

  return (
    <PageSeo
      canonical={canonicalUrl}
      description={description}
      openGraph={{
        images: imageUrl ? [{ url: imageUrl }] : [],
        type: "article",
        url: canonicalUrl,
      }}
      title={video.headline ?? undefined}
    />
  );
};

VideoSeo.displayName = "VideoSeo";
